const CAMPAIGN_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "utm_id", "utm_source_platform", "utm_creative_format", "utm_marketing_tactic",
  "gclid", "gbraid", "wbraid", "dclid", "msclkid", "fbclid", "ttclid", "li_fat_id",
] as const;
const STORAGE_KEY = "dbm-campaign-attribution-v1";

let capturedDocumentEntry = false;

type CampaignStorage = Pick<Storage, "getItem" | "setItem">;

/** Capture known campaign identifiers only; arbitrary query strings can contain personal data. */
export function campaignAttribution(href: string): Record<string, string> {
  const url = new URL(href);
  const values: Record<string, string> = { landing_page: url.pathname };
  for (const key of CAMPAIGN_KEYS) {
    const value = url.searchParams.get(key)?.trim();
    if (value) values[key] = value.slice(0, 500);
  }
  return values;
}

/** Keep the latest tagged/external entry in this tab through internal navigation.
 * Referrer origin only: never save its path, query, fragment, or credentials.
 */
export function rememberedCampaignAttribution(
  href: string,
  providedStorage?: CampaignStorage,
  entry?: { referrer: string; isNewDocument: boolean },
): Record<string, string> {
  const current = campaignAttribution(href);
  if (!entry && typeof document !== "undefined") {
    entry = { referrer: document.referrer, isNewDocument: !capturedDocumentEntry };
    capturedDocumentEntry = true;
  }
  let externalReferrer = "";
  try {
    const referrer = new URL(entry?.referrer ?? "");
    const currentUrl = new URL(href);
    const ownHost = (host: string) => host.replace(/^www\./, "");
    if (["http:", "https:"].includes(referrer.protocol) && ownHost(referrer.hostname) !== ownHost(currentUrl.hostname)) {
      externalReferrer = referrer.origin;
    }
  } catch { /* No referrer was supplied by the browser. */ }
  if (entry) {
    current.referrer = externalReferrer;
    current.attribution_capture = "entry-v1";
  }
  let storage = providedStorage;
  try {
    storage ??= typeof window === "undefined" ? undefined : window.sessionStorage;
    if (!storage) return current;

    let parsed: unknown = null;
    try {
      parsed = JSON.parse(storage.getItem(STORAGE_KEY) ?? "null");
    } catch {
      // Replace malformed stale data with the current approved fields.
    }
    const previous: Record<string, string> = {};
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      for (const key of ["landing_page", "referrer", "attribution_capture", ...CAMPAIGN_KEYS]) {
        const value = (parsed as Record<string, unknown>)[key];
        if (typeof value === "string" && value.length <= 500 && (key !== "landing_page" || (value.startsWith("/") && !value.startsWith("//")))) {
          previous[key] = value;
        }
      }
    }

    const hasNewCampaign = CAMPAIGN_KEYS.some((key) => key in current);
    const newExternalEntry = entry?.isNewDocument && Boolean(externalReferrer);
    const result = hasNewCampaign || newExternalEntry || !previous.landing_page ? current : previous;
    storage.setItem(STORAGE_KEY, JSON.stringify(result));
    return result;
  } catch {
    // Private browsing can block storage; a tagged form page can still capture its own URL.
    return current;
  }
}
